import {
  required,
  email,
  createI18nMessage,
  sameAs,
  maxLength,
} from '@vuelidate/validators'
import { getPayloadDate, dateValidator } from '@scayle/storefront-nuxt'
import { snakeCase } from 'es-toolkit'

const isValidDate = (date: Date) => new Date(date).toString() !== 'Invalid Date'

// This needs to be aligned with CO frontend configuration in Panel
const validatePassword = (value: string): boolean => {
  const pattern = /^.{6,70}$/.test(value)
  return pattern && !value.includes(' ')
}

const normalizeDate = (value: string): string => {
  return value.replaceAll('/', '.')
}

const validateDate = (value: string): boolean =>
  dateValidator(normalizeDate(value))

const validateFutureDate = (value: string): boolean => {
  const formattedDate = getPayloadDate(normalizeDate(value))
  if (!formattedDate) {
    return true
  }
  const date = new Date(formattedDate)
  if (!isValidDate(date)) {
    return true
  }
  const now = new Date()
  return date.getTime() < now.getTime()
}

const validateName = (value: string | undefined): boolean => {
  if (!value) {
    return true
  }
  const hasValidUnicodeLetters = /^[\p{Letter}\s'-.]+$/u.test(value)
  const hasPunctuationStreak = /['-.]{2,}/.test(value)
  const hasValidBoundaries = !/^['-.].*|['.-]$/.test(value)
  return hasValidUnicodeLetters && hasValidBoundaries && !hasPunctuationStreak
}

export type Rule = {
  required: typeof required
  email: typeof email
  password: typeof validatePassword
  date: typeof validateDate
  sameAs: typeof sameAs
  maxLength: typeof maxLength
  futureDate: typeof validateFutureDate
  name: typeof validateName
}

export default defineNuxtPlugin((nuxtApp) => {
  const { $i18n } = useNuxtApp()
  const withI18nMessage = createI18nMessage({
    t: $i18n.t.bind($i18n),
    messagePath: ({ $validator }) => `validation.${snakeCase($validator)}`,
    // @ts-expect-error - TS ignore is needed since the return type is not correct here.
    // https://github.com/vuelidate/vuelidate/issues/1180
    messageParams: ({ field, max, otherName, property, ...params }) => ({
      ...params,
      field: $i18n.t(`form_fields.${snakeCase(field || property)}`),
      max,
      otherField: otherName,
    }),
  })

  const rule = {
    required: withI18nMessage(required),
    email: withI18nMessage(email),
    date: withI18nMessage(validateDate),
    password: withI18nMessage(validatePassword),
    futureDate: withI18nMessage(validateFutureDate),
    name: withI18nMessage(validateName),
    sameAs: withI18nMessage(sameAs, { withArguments: true }),
    maxLength: withI18nMessage(maxLength, { withArguments: true }),
  }

  nuxtApp.provide('validation', { rule })
})
